import React from 'react'
import { useTranslation } from 'react-i18next'

import styled from 'styled-components'
import nativeStyled from 'styled-components/native'

import { faIdBadge } from '@fortawesome/pro-light-svg-icons'

import { FaIcon } from '../../../App/Components'
import { useHistory, useParams } from '../../../Router'
import StyleVariables from '../../../StyleVariables'
import { useAppSelector } from '../../../store/hooks'
import {
  IPreviousAppointment,
  IPreviousAppointmentActivity,
  PreviousAppointmentActivityPerson,
} from '../../../PreviousAppointments/IPreviousAppointment'
import { IMenuActivity } from '../../../Menu/Store/IMenuActivity'

/**
 * OB-499: Native <button>. Was styled(TouchableOpacity).
 */
const ButtonContainer = styled.button<{ color: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 8px ${StyleVariables.horizontalGutter};
  border: 1px solid ${({ color }) => color};
  border-radius: ${StyleVariables.borderRadiusPill};
  margin: ${StyleVariables.cardMargin};
  align-self: center;
  width: fit-content;
  background: transparent;
  appearance: none;
  cursor: pointer;
  font: inherit;
`

const ButtonText = nativeStyled.Text<{ color: string }>`
  font-size: ${StyleVariables.fontSizeBase};
  color: ${({ color }) => color};
  margin-left: 10px;
`

const preferredPersonCheck = (
  appointment: IPreviousAppointment | null
): PreviousAppointmentActivityPerson | null => {
  if (appointment) {
    const firstActivity = appointment.activities[0]
    if (!firstActivity.persons.length) return null
    return firstActivity.persons.find(p => p.isPreferred) || null
  }
  return null
}

type Props = {
  isPersonSelectable: boolean
  moveAppointmentData?: IPreviousAppointmentActivity[]
}

export const SelectPersonForAllActivities = ({
  isPersonSelectable,
  moveAppointmentData,
}: Props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { establishmentId } = useParams<{ [key: string]: string }>()

  const {
    settings,
    selectedPerson,
    persons,
    customerActivities,
    editAppointmentPerson,
  } = useAppSelector((state) => ({
    settings: state.settings.values,
    selectedPerson: state.appointment.selectedPerson,
    persons: state.persons.persons,
    customerActivities: state.appointment.customers
      .map((customer) => customer.activities)
      .flat(1),
    editAppointmentPerson: preferredPersonCheck(
      state.appointment.editPreviousAppointment
    ),
  }))

  const dataSwitch = moveAppointmentData || customerActivities
  const personNameSwitch = moveAppointmentData
    ? editAppointmentPerson
    : selectedPerson

  const selectPersonNavigation = () =>
    moveAppointmentData
      ? history.push(`/${establishmentId}/edit-person`)
      : history.push(`/${establishmentId}/select-person`, { returnPath: `/${establishmentId}/appointment/date` })
  if (!isPersonSelectable) {
    return null
  }
  if (
    typeof settings !== 'undefined' &&
    settings.isPreferredPersonPerActivity
  ) {
    return null
  }
  if (!persons) {
    return null
  }

  const filteredPersons = persons.filter((person) => {
    if (
      !dataSwitch ||
      person.isCabine ||
      !person.isVisibleOnline ||
      !person.isVisibleInCalender
    ) {
      return false
    }
    return dataSwitch.some(
      (activity: IPreviousAppointmentActivity | IMenuActivity) =>
        person.activityIds.includes(activity.id) && activity.isPersonSelectable
    )
  })

  if (filteredPersons.length === 0) {
    return null
  }

  return (
    <ButtonContainer
      type="button"
      color={settings.highlightColor}
      onClick={selectPersonNavigation}
    >
      <FaIcon size="lg" icon={faIdBadge} color={settings.highlightColor} />
      <ButtonText color={settings.highlightColor}>
        {personNameSwitch ? personNameSwitch.firstName : t('Choose person')}
      </ButtonText>
    </ButtonContainer>
  )
}
